import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified enquiry capture. Wraps any element as a clickable trigger that
 * opens a modal form. On submit, the enquiry is POSTed to /api/enquiries,
 * which persists it to MongoDB and notifies info@cleankinservices.co.uk.
 *
 * Replaces raw `mailto:` links so we never lose an enquiry to a missing /
 * misconfigured mail client.
 *
 * Usage:
 *   <EnquiryDialog source="header" subject="General enquiry">
 *     <Button>Email us</Button>
 *   </EnquiryDialog>
 */
export const EnquiryDialog = ({
  children,
  source = "website",
  subject: defaultSubject = "General enquiry",
  defaultMessage = "",
  triggerAsChild = true,
}) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: defaultSubject,
    message: defaultMessage,
  });

  const resetAndClose = () => {
    setOpen(false);
    // Delay reset so the close animation doesn't show a flicker
    setTimeout(() => {
      setSubmitted(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: defaultSubject,
        message: defaultMessage,
      });
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || defaultSubject,
        message: form.message.trim(),
        source,
      });
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch within 1 business day.");
    } catch (err) {
      logError("Enquiry submission failed:", err);
      toast.error(
        err?.response?.data?.detail || "Could not send your enquiry. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={triggerAsChild} data-testid={`enquiry-trigger-${source}`}>
        {children}
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-lg bg-white"
        data-testid="enquiry-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A2E12]">
            <Mail className="w-5 h-5 text-[#2D5016]" />
            {submitted ? "Message received" : "Send us a message"}
          </DialogTitle>
          <DialogDescription className="text-[#5C6B57]">
            {submitted
              ? "Thanks — a member of our team will be in touch within 1 business day."
              : "Drop us a line and we'll get back to you within 1 business day."}
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-6" data-testid="enquiry-success">
            <CheckCircle2 className="w-14 h-14 text-[#7CB342] mx-auto mb-4" />
            <p className="text-[#1A2E12] font-semibold mb-2">
              Your message is on its way to our team.
            </p>
            <p className="text-sm text-[#5C6B57] mb-4">
              We've sent a copy to {form.email} so you have it on file.
            </p>
            <Button
              onClick={resetAndClose}
              className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-6"
              data-testid="enquiry-close"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="enquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-[#5C6B57]">Name *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="mt-1"
                  data-testid="enquiry-name"
                />
              </div>
              <div>
                <Label className="text-sm text-[#5C6B57]">Email *</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="mt-1"
                  data-testid="enquiry-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-[#5C6B57]">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className="mt-1"
                  data-testid="enquiry-phone"
                />
              </div>
              <div>
                <Label className="text-sm text-[#5C6B57]">Subject</Label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                  className="mt-1"
                  data-testid="enquiry-subject"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#5C6B57]">How can we help? *</Label>
              <Textarea
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                className="mt-1"
                placeholder="Tell us a bit about what you're looking for..."
                data-testid="enquiry-message"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="rounded-full"
                data-testid="enquiry-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
                data-testid="enquiry-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send message"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnquiryDialog;
